import React, { useEffect, useRef, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import { motion } from 'framer-motion';
import {
  CreditCard, Edit, Home, Loader2, Map, MessageSquare,
  Network, Newspaper, Plus, Save, Search, Settings, Trash2, TrendingUp,
  Upload, Users, Wrench, X
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import 'react-quill/dist/quill.snow.css';
import ktcLogo from '../../assets/ktc.png';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import StaffPageHeader from '../../components/StaffPageHeader';
import { stripHtml, toRichHtml } from '../../utils/newsContent';

const API = `${resolveBackendBaseUrl()}/api`;
const BASE_URL = `${resolveBackendBaseUrl()}`.replace(/\/$/, '');

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'blockquote', 'code-block',
  'link', 'image'
];

const AdminNews = () => {
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    image_url: '',
    author: ''
  });

  const categories = ['Teknologi', 'Promo', 'Layanan', 'Tips & Trik', 'Pengumuman', 'Event'];
  const toText = (value) => String(value ?? '').trim();

  useEffect(() => {
    const storedUser = localStorage.getItem('ktc_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchNews();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/news`);
      const data = Array.isArray(response.data) ? response.data : [];
      setNews(data);
    } catch (error) {
      toast.error('Gagal memuat berita');
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (newsItem = null) => {
    if (newsItem) {
      setEditingNews(newsItem);
      setFormData({
        title: newsItem.title || '',
        content: toRichHtml(newsItem.content || ''),
        category: newsItem.category || '',
        image_url: newsItem.image_url || '',
        author: newsItem.author || ''
      });
    } else {
      setEditingNews(null);
      setFormData({
        title: '',
        content: '',
        category: '',
        image_url: '',
        author: user?.name || ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNews(null);
    setFormData({ title: '', content: '', category: '', image_url: '', author: '' });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Construct full URL
      const uploadedUrl = String(response.data?.url || '').trim();
      const imageUrl = uploadedUrl.startsWith('http') ? uploadedUrl : `${BASE_URL || ''}${uploadedUrl}`;
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      toast.success('Gambar berhasil diupload!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengupload gambar');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const plainContent = stripHtml(formData.content);
    if (!plainContent) {
      toast.error('Konten berita wajib diisi');
      return;
    }

    try {
      if (editingNews) {
        await axios.put(`${API}/news/${editingNews.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Berita berhasil diperbarui!');
      } else {
        await axios.post(`${API}/news`, formData, { headers: getAuthHeaders() });
        toast.success('Berita berhasil ditambahkan!');
      }
      handleCloseModal();
      fetchNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan berita');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus berita ini?')) return;
    
    try {
      await axios.delete(`${API}/news/${id}`, { headers: getAuthHeaders() });
      toast.success('Berita berhasil dihapus!');
      fetchNews();
    } catch (error) {
      toast.error('Gagal menghapus berita');
    }
  };

  const filteredNews = news.filter((item) => {
    const plainContent = stripHtml(item.content || '').toLowerCase();
    const query = toText(searchQuery).toLowerCase();

    return (
      toText(item.title).toLowerCase().includes(query) ||
      toText(item.category).toLowerCase().includes(query) ||
      plainContent.includes(query)
    );
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <>
      {loading && (
        <StaffPageLoadingOverlay text="Memuat data berita..." />
      )}

      {/* Main Content */}
      <AppShell>
      <PageContainer>
        {/* Header */}
        <StaffPageHeader
          className="mb-8"
          eyebrow="Konten"
          title="Berita"
          subtitle="Tambah, edit, atau hapus artikel berita."
          actions={(
            <Button
              onClick={() => handleOpenModal()}
              className="btn-primary flex items-center gap-2 text-white"
              data-testid="add-news-button"
            >
              <Plus className="w-5 h-5" />
              Tambah Berita
            </Button>
          )}
        />

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input
            type="text"
            placeholder="Cari berita..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 text-white max-w-md"
            data-testid="search-news-input"
          />
        </div>

        {/* News List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-brand-blue border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="text-center py-20 bg-[#121212] rounded-xl border border-white/10">
            <Newspaper className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Belum ada berita</p>
            <p className="text-gray-500 text-sm mt-2">Klik tombol "Tambah Berita" untuk membuat artikel baru</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNews.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#121212] rounded-xl p-4 border border-white/10 flex flex-col sm:flex-row items-start gap-4"
                data-testid={`news-item-${item.id}`}
              >
                <img 
                  src={item.image_url || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=200'}
                  alt=""
                  className="w-full sm:w-32 h-32 rounded-lg object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-brand-red/20 text-brand-red text-xs rounded">
                      {toText(item.category) || 'Umum'}
                    </span>
                    <span className="text-gray-500 text-xs">{formatDate(item.created_at)}</span>
                  </div>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-2 line-clamp-1">
                    {toText(item.title) || 'Tanpa Judul'}
                  </h3>
                  <p className="text-gray-400 text-sm line-clamp-2 mb-2">{stripHtml(item.content || '')}</p>
                  <p className="text-gray-500 text-xs">Oleh: {toText(item.author) || '-'}</p>
                </div>
                <div className="flex sm:flex-col gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenModal(item)}
                    className="btn-secondary"
                    data-testid={`edit-news-${item.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(item.id)}
                    className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                    data-testid={`delete-news-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
                  </PageContainer>
    </AppShell>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {editingNews ? 'Edit Berita' : 'Tambah Berita Baru'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div>
              <label className="staff-form-label">Judul</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Judul berita"
                required
                className="bg-[#1a1a1a] border-white/10 text-white"
                data-testid="news-title-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="staff-form-label">Kategori</label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-white/10 text-white" data-testid="news-category-select">
                    <SelectValue placeholder="Pilih kategori" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat} className="text-white hover:bg-white/10">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="staff-form-label">Penulis</label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="Nama penulis"
                  required
                  className="bg-[#1a1a1a] border-white/10 text-white"
                  data-testid="news-author-input"
                />
              </div>
            </div>

            <div>
              <label className="staff-form-label">Gambar</label>
              <div className="space-y-3">
                {/* Upload Button */}
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="btn-secondary flex-1"
                    data-testid="upload-image-button"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Mengupload...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Gambar
                      </>
                    )}
                  </Button>
                </div>

                {/* OR URL Input */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-[#121212] px-2 text-gray-500">atau masukkan URL</span>
                  </div>
                </div>

                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="bg-[#1a1a1a] border-white/10 text-white"
                  data-testid="news-image-input"
                />

                {/* Preview */}
                {formData.image_url && (
                  <div className="relative">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-40 object-cover rounded-lg"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="staff-form-label">Konten</label>
              <div className="news-editor" data-testid="news-content-input">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={(value) => setFormData({ ...formData, content: value })}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Tulis konten berita di sini..."
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={handleCloseModal} className="btn-secondary">
                <X className="w-4 h-4 mr-2" />
                Batal
              </Button>
              <Button type="submit" className="btn-primary text-white" data-testid="save-news-button">
                <Save className="w-4 h-4 mr-2" />
                {editingNews ? 'Simpan Perubahan' : 'Tambah Berita'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminNews;














